import { Mail } from "lucide-react";
import { ISLAND_BY_SLUG } from "../../lib/constants";
import { ScraperReliabilityCard } from "./ScraperReliabilityCard";
import { TrafficSourcesCard } from "./TrafficSourcesCard";

const Stat = ({ label, value, testid }) => (
  <div className="card-soft p-5" data-testid={testid}>
    <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-2">
      {label}
    </div>
    <div className="font-serif-display text-4xl text-[#0AADA0]">{value}</div>
  </div>
);

const FunnelCard = ({ pageviews, newWhatsApp, newPush, newWaitlist }) => {
  const pct = (n) =>
    pageviews > 0 ? ((n / pageviews) * 100).toFixed(2) + "%" : "—";
  const rows = [
    { label: "Pageviews", value: pageviews, conv: null, testid: "funnel-pv" },
    { label: "New WhatsApp subs", value: newWhatsApp, conv: pct(newWhatsApp), testid: "funnel-wa" },
    { label: "New push subs", value: newPush, conv: pct(newPush), testid: "funnel-push" },
    { label: "Waitlist signups", value: newWaitlist, conv: pct(newWaitlist), testid: "funnel-waitlist" },
  ];
  return (
    <div className="card-soft p-5" data-testid="growth-funnel-card">
      <div className="flex items-baseline justify-between mb-4">
        <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280]">
          Growth funnel · last 7 days
        </h3>
        <span className="text-xs text-[#6B7280]">conversion vs. pageviews</span>
      </div>
      <ul className="space-y-3">
        {rows.map((r) => (
          <li key={r.label} className="flex items-center gap-3 text-sm" data-testid={r.testid}>
            <span className="text-[#1A1A1A] w-40 flex-shrink-0">{r.label}</span>
            <span className="font-serif-display text-2xl text-[#0AADA0] w-24 text-right">
              {r.value.toLocaleString()}
            </span>
            <span className="text-xs text-[#6B7280] w-20 text-right">{r.conv || ""}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const TrendCard = ({ title, series, color = "#0AADA0", testid }) => {
  const total = series.reduce((s, p) => s + (p.count || 0), 0);
  const max = Math.max(1, ...series.map((p) => p.count || 0));
  const W = 600, H = 120, P = 8;
  const stepX = (W - 2 * P) / Math.max(1, series.length - 1);
  const points = series.map((p, i) => {
    const x = P + i * stepX;
    const y = H - P - ((p.count || 0) / max) * (H - 2 * P);
    return [x, y];
  });
  const line = points.map((pt, i) => `${i === 0 ? "M" : "L"}${pt[0].toFixed(1)},${pt[1].toFixed(1)}`).join(" ");
  const areaBottom = H - P;
  const area = points.length
    ? `M${points[0][0].toFixed(1)},${areaBottom} ` +
      points.map((pt) => `L${pt[0].toFixed(1)},${pt[1].toFixed(1)}`).join(" ") +
      ` L${points[points.length - 1][0].toFixed(1)},${areaBottom} Z`
    : "";
  const first = series[0]?.date?.slice(5) || "";
  const last  = series[series.length - 1]?.date?.slice(5) || "";
  return (
    <div className="card-soft p-5" data-testid={testid}>
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280]">{title}</h3>
        <span className="text-xs text-[#0AADA0] font-medium" data-testid={`${testid}-total`}>
          {total.toLocaleString("en-US")} total
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-28">
        <defs>
          <linearGradient id={`grad-${testid}`} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%"  stopColor={color} stopOpacity="0.25" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        {area && <path d={area} fill={`url(#grad-${testid})`} />}
        {line && <path d={line} fill="none" stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />}
      </svg>
      <div className="flex justify-between text-[10px] text-[#6B7280] mt-1 font-mono">
        <span>{first}</span>
        <span>{last}</span>
      </div>
    </div>
  );
};

const BreakdownCard = ({ title, items, testid }) => {
  const max = Math.max(1, ...items.map((i) => i.count));
  const total = items.reduce((s, i) => s + (i.count || 0), 0);
  return (
    <div className="card-soft p-5" data-testid={testid}>
      <div className="flex items-baseline justify-between mb-4">
        <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280]">{title}</h3>
        <span className="text-xs text-[#6B7280]">{total} total</span>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-[#6B7280]">No data yet.</p>
      ) : (
        <ul className="space-y-2">
          {items.map((it, i) => (
            <li key={i} className="flex items-center gap-3 text-sm">
              <span className="text-[#1A1A1A] w-36 flex-shrink-0 truncate">{it.label}</span>
              <div className="flex-1 h-1.5 bg-[#E5E5E5] rounded-full overflow-hidden">
                <div className="h-full bg-[#0AADA0] rounded-full" style={{ width: `${(it.count / max) * 100}%` }} />
              </div>
              <span className="text-xs text-[#6B7280] w-8 text-right">{it.count}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const UnderperformingCard = ({ items }) => {
  const fmt = (iso) => {
    if (!iso) return "";
    const t = Date.parse(iso);
    if (isNaN(t)) return "";
    const diffMin = Math.floor((Date.now() - t) / 60000);
    if (diffMin < 60) return `${diffMin}m ago`;
    return `${Math.floor(diffMin / 60)}h ago`;
  };
  return (
    <div className="card-soft p-5" data-testid="underperforming-card">
      <div className="flex items-baseline justify-between mb-4">
        <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280] flex items-center gap-2">
          <span>🚨</span>
          <span>Underperforming · last 24h (0 pageviews)</span>
        </h3>
        <span className="text-xs text-[#6B7280]">{items.length} flagged</span>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-[#6B7280]">Every fresh story picked up at least one read — nice work.</p>
      ) : (
        <ul className="divide-y divide-[#F5F5F5]">
          {items.map((a, i) => (
            <li key={a.id} className="py-2 flex items-center gap-3 text-sm" data-testid={`underperforming-item-${i}`}>
              <span className="text-[11px] text-[#6B7280] font-mono w-5">{i + 1}.</span>
              <a href={`/article/${a.id}`} target="_blank" rel="noopener noreferrer"
                 className="flex-1 min-w-0 text-[#1A1A1A] truncate hover:text-[#0AADA0]">
                {a.title}
              </a>
              {a.island && ISLAND_BY_SLUG[a.island] && (
                <span className="text-xs text-[#6B7280] shrink-0" title={ISLAND_BY_SLUG[a.island].name}>
                  {ISLAND_BY_SLUG[a.island].flag} {ISLAND_BY_SLUG[a.island].name}
                </span>
              )}
              <span className="text-xs text-[#6B7280] shrink-0 w-16 text-right">{fmt(a.published_at)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const WaitlistCard = ({ total, recent }) => {
  const fmt = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d)) return "";
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };
  return (
    <div className="card-soft p-5" data-testid="waitlist-card">
      <div className="flex items-baseline justify-between mb-4">
        <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280] flex items-center gap-2">
          <Mail size={12} /> Waitlist · newest signups
        </h3>
        <span className="text-xs text-[#6B7280]" data-testid="waitlist-total">{total} total</span>
      </div>
      {recent.length === 0 ? (
        <p className="text-sm text-[#6B7280]">
          No waitlist signups yet. Add &ldquo;Coming Next&rdquo; islands to start capturing demand.
        </p>
      ) : (
        <ul className="divide-y divide-[#F5F5F5]">
          {recent.map((w, i) => {
            const island = w.island_slug ? ISLAND_BY_SLUG[w.island_slug] : null;
            return (
              <li key={`${w.email}-${w.island_slug || "all"}-${i}`}
                  className="py-2.5 flex items-center gap-3 text-sm"
                  data-testid={`waitlist-row-${i}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-[#1A1A1A] truncate">
                    {w.name ? <span className="font-medium">{w.name}</span> : null}
                    {w.name ? <span className="text-[#6B7280]"> · </span> : null}
                    <span className="text-[#1A1A1A]">{w.email}</span>
                  </div>
                  <div className="text-xs text-[#6B7280] mt-0.5 flex items-center gap-2">
                    <span>
                      {island ? `${island.flag} ${island.name}`
                        : w.island_slug ? `🏝️ ${w.island_slug}` : "🌍 Any future launch"}
                    </span>
                    <span>·</span>
                    <span>{fmt(w.created_at)}</span>
                  </div>
                </div>
                <a href={`mailto:${w.email}`}
                   className="text-xs text-[#0AADA0] hover:underline shrink-0"
                   data-testid={`waitlist-email-${i}`}>
                  Email
                </a>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export const OverviewTab = ({ analytics }) => {
  if (!analytics) return null;
  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat label="Pageviews today"   value={analytics.pageviews_today}    testid="stat-pageviews" />
        <Stat label="Pageviews · 30d"    value={analytics.pageviews_30d ?? 0} testid="stat-pageviews-30d" />
        <Stat label="WhatsApp subs"      value={analytics.total_subscribers}  testid="stat-subscribers" />
        <Stat label="Push subs"          value={analytics.push_subscribers ?? 0} testid="stat-push-subscribers" />
        <Stat label="Total articles"     value={analytics.total_articles}     testid="stat-articles" />
      </div>

      <FunnelCard
        pageviews={analytics.pageviews_7d ?? 0}
        newWhatsApp={analytics.new_subs_7d ?? 0}
        newPush={analytics.new_push_subs_7d ?? 0}
        newWaitlist={analytics.new_waitlist_7d ?? 0}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TrendCard title="Pageviews · last 30 days" series={analytics.pageviews_by_day || []} color="#0AADA0" testid="trend-pageviews" />
        <TrendCard title="New subscribers · last 30 days" series={analytics.subscribers_by_day || []} color="#E63946" testid="trend-subscribers" />
      </div>

      <ScraperReliabilityCard />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <BreakdownCard
          title="Subscribers by digest language"
          items={(analytics.subscribers_by_lang || []).map((r) => ({
            label: r.lang === "nl" ? "🇳🇱 Dutch"
                 : r.lang === "es" ? "🇪🇸 Spanish"
                 : "🇬🇧 English",
            count: r.count,
          }))}
          testid="breakdown-lang"
        />
        <BreakdownCard
          title="Subscribers by island preference"
          items={(analytics.subscribers_by_island || []).map((r) => {
            const m = ISLAND_BY_SLUG[r.island];
            return { label: r.island === "all" ? "🌍 All Islands" : `${m?.flag || ""} ${m?.name || r.island}`, count: r.count };
          })}
          testid="breakdown-island"
        />
      </div>

      <BreakdownCard
        title="Top traffic sources · last 30 days"
        items={(analytics.top_referrers || []).map((r) => ({
          label: r.domain === "direct" ? "🔗 Direct / bookmarks" : `🌐 ${r.domain}`,
          count: r.count,
        }))}
        testid="breakdown-referrers"
      />

      <TrafficSourcesCard />

      <WaitlistCard total={analytics.waitlist_total || 0} recent={analytics.waitlist_recent || []} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-soft p-5">
          <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-4">
            Top articles · last 30 days
          </h3>
          {(analytics.top_articles_30d || []).length === 0 ? (
            <p className="text-sm text-[#6B7280]">No pageviews yet.</p>
          ) : (
            <ul className="space-y-2.5">
              {analytics.top_articles_30d.map((a, idx) => (
                <li key={a.id} className="flex items-center gap-3 text-sm">
                  <span className="text-[11px] text-[#6B7280] font-mono w-5">{idx + 1}.</span>
                  <span className="text-[#1A1A1A] flex-1 truncate">{a.title}</span>
                  {a.island && ISLAND_BY_SLUG[a.island] && (
                    <span className="text-xs" title={ISLAND_BY_SLUG[a.island].name}>
                      {ISLAND_BY_SLUG[a.island].flag}
                    </span>
                  )}
                  <span className="text-xs text-[#0AADA0] font-medium w-10 text-right">{a.views}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="card-soft p-5">
          <h3 className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-4">
            Articles by category
          </h3>
          <ul className="space-y-2">
            {analytics.by_category.map((c) => (
              <li key={c.name} className="flex items-center justify-between text-sm gap-3">
                <span className="text-[#1A1A1A] w-32 flex-shrink-0">{c.name}</span>
                <div className="flex-1 h-1.5 bg-[#E5E5E5] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#0AADA0] rounded-full"
                    style={{
                      width: `${Math.min(100, (c.count / Math.max(...analytics.by_category.map((x) => x.count))) * 100)}%`,
                    }}
                  />
                </div>
                <span className="text-xs text-[#6B7280] w-8 text-right">{c.count}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <UnderperformingCard items={analytics.underperforming || []} />
    </div>
  );
};
